// 問題回報（plan SPEC #support-business，Eddy 2026-10-21 選單）：組織的 IT / 管理者在 VMS 回報問題、看本組織的回報。
//   /support          清單：全部 / 我開的 / 我關注的 / 本組織的（只有一種有東西時不畫分段）
//   /support/new      新回報（?home=&aid= 從設備詳細頁〔回報這台的問題〕帶設備）
//   /support/:id      時間軸、留言、附件
// 入口在頁首使用者選單與設備詳細頁，只對有回報權的人（owner / org_admin / org.support）顯示；沒有回報權的人直接開網址看到說明。
// 畫面用詞「問題回報」，不寫「工單」「開發者」（SPEC #support-business 入口）。
import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KinioError } from '@kinio/web-sdk';
import { useApp } from '../../lib/AppContext';
import { useScope } from '../../lib/scope';
import { webBuildLabel } from '../shell/UserMenu';
import {
  SUPPORT_ATTACH_TYPES,
  SUPPORT_CATEGORIES,
  SUPPORT_MAX_BYTES,
  SUPPORT_MAX_FILES,
  SUPPORT_SCOPES,
  contextJson,
  reporterApis,
  reporterOrgs,
  supportApi,
  supportContext,
  type SupportScope,
  type SupportTicket,
} from '../../lib/supportApi';

export function SupportPage() {
  const { t } = useTranslation();
  const { orgs, booting } = useScope();
  const mine = reporterOrgs(orgs);
  if (booting) return <p className="muted">{t('common.loading')}</p>;
  if (mine.length === 0)
    return (
      <div data-testid="support-none">
        <h1 className="page-title">{t('support.title')}</h1>
        <p className="muted">{t('support.no_right')}</p>
      </div>
    );
  return (
    <Routes>
      <Route index element={<SupportList />} />
      <Route path="new" element={<SupportNew />} />
      <Route path=":id" element={<SupportDetail />} />
    </Routes>
  );
}

function statusBadge(s: string): string {
  if (s === 'resolved' || s === 'closed') return 'badge badge-ok';
  if (s === 'open') return 'badge badge-warn';
  return 'badge';
}

/** 有回報權的組織所在 plane 的回報（通常只有一個 plane），依時間新到舊、依 ticket_id 去重。 */
function useSupportList(scope: SupportScope) {
  const { orgs } = useScope();
  const apis = reporterApis(orgs);
  return useQuery({
    queryKey: ['support', scope, apis.join(',')],
    queryFn: async () => {
      const lists = await Promise.all(apis.map((api) => supportApi.list(api, scope).then((ts) => ts.map((x) => ({ ...x, api })))));
      const seen = new Set<string>();
      return lists
        .flat()
        .filter((x) => (seen.has(x.ticket_id) ? false : (seen.add(x.ticket_id), true)))
        .sort((a, b) => b.created_at.localeCompare(a.created_at));
    },
  });
}

function SupportList() {
  const { t } = useTranslation();
  const [scope, setScope] = useState<SupportScope>('all');
  const all = useSupportList('all');
  const q = useSupportList(scope);
  const rels = new Set((all.data ?? []).map((x) => x.relation));
  const counts = (s: SupportScope) =>
    s === 'all' ? (all.data ?? []).length : (all.data ?? []).filter((x) => x.relation === (s === 'mine' ? 'owner' : s === 'watching' ? 'watcher' : 'org')).length;
  const rows = scope === 'all' ? all.data : q.data;
  return (
    <div data-testid="support-list">
      <div className="toolbar wrap">
        <h1 className="page-title">{t('support.title')}</h1>
        <span style={{ flex: 1 }} />
        <Link className="btn btn-primary" to="/support/new" data-testid="support-new">
          {t('support.new')}
        </Link>
      </div>
      <p className="muted small">{t('support.hint')}</p>
      {rels.size > 1 && (
        <div className="seg" role="group" aria-label={t('support.filter')}>
          {SUPPORT_SCOPES.map((s) => (
            <button key={s} type="button" aria-pressed={scope === s} onClick={() => setScope(s)} data-testid={`support-scope-${s}`}>
              {t(`support.scope_${s}`)} {counts(s)}
            </button>
          ))}
        </div>
      )}
      {(all.isLoading || (scope !== 'all' && q.isLoading)) && <p className="muted">{t('common.loading')}</p>}
      {all.isError && <p className="error">{t('support.load_failed')}</p>}
      {all.isSuccess && (rows ?? []).length === 0 && <p className="muted">{t('support.empty')}</p>}
      <ul className="plain-list support-rows">
        {(rows ?? []).map((x) => (
          <li key={x.ticket_id} className="card" data-testid="support-row">
            <Link to={`/support/${encodeURIComponent(x.ticket_id)}`} style={{ fontWeight: x.unread ? 700 : undefined }}>
              {x.subject}
            </Link>{' '}
            <span className={statusBadge(x.status)}>{t(`support.status_${x.status}`, { defaultValue: x.status })}</span>
            {x.unread && <span className="badge badge-warn">{t('support.new_reply')}</span>}
            <div className="muted small">
              {t(`support.cat_${x.category}`, { defaultValue: x.category })} · {new Date(x.created_at).toLocaleString()}
              {x.relation !== 'owner' && ` · ${t('support.by', { name: x.owner_name || '-', org: x.org_name || '-' })}`}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function errMsg(t: (k: string, o?: Record<string, unknown>) => string, e: unknown): string {
  if (e instanceof KinioError) return t(`support.err_${e.code}`, { defaultValue: t('support.err_generic', { code: e.code }) });
  return t('support.err_generic', { code: '-' });
}

function SupportNew() {
  const { t, i18n } = useTranslation();
  const { config } = useApp();
  const { orgs, devices } = useScope();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const mine = reporterOrgs(orgs);
  const [orgId, setOrgId] = useState(mine[0]?.org_id ?? '');
  const org = mine.find((o) => o.org_id === orgId) ?? mine[0];
  const [category, setCategory] = useState<string>(params.get('aid') ? 'offline' : 'other');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [devKey, setDevKey] = useState(params.get('aid') ? `${params.get('home') ?? ''}/${params.get('aid')}` : '');
  const [files, setFiles] = useState<File[]>([]);
  const [fileErr, setFileErr] = useState('');
  const [override, setOverride] = useState('');
  const [blocked, setBlocked] = useState<string | null>(null);
  const [failedUploads, setFailedUploads] = useState(0);
  // 只列同一個 plane、看得到的設備（device.read 才會在「我的設備」裡）
  const devs = useMemo(() => devices.filter((d) => !org || d.api === org.api), [devices, org]);
  const dev = devs.find((d) => `${d.home_id}/${d.device_ref}` === devKey) ?? null;
  const build = webBuildLabel(config.build, typeof __WEB_BUILD__ === 'string' ? __WEB_BUILD__ : undefined);
  const ctx = org
    ? supportContext({
        version: config.version,
        build,
        org,
        device: dev ? { aid: dev.device_ref, name: dev.display_name, model: dev.model, online: dev.online } : null,
        page: (params.get('from') || loc.pathname) ?? '',
        language: i18n.language,
      })
    : null;

  const pick = (list: FileList | null) => {
    setFileErr('');
    const next = [...files];
    for (const f of Array.from(list ?? [])) {
      if (!SUPPORT_ATTACH_TYPES.includes(f.type)) {
        setFileErr(t('support.file_type', { name: f.name }));
        continue;
      }
      if (f.size > SUPPORT_MAX_BYTES) {
        setFileErr(t('support.file_big', { name: f.name }));
        continue;
      }
      if (next.length >= SUPPORT_MAX_FILES) {
        setFileErr(t('support.file_many', { n: SUPPORT_MAX_FILES }));
        break;
      }
      next.push(f);
    }
    setFiles(next);
  };

  const m = useMutation({
    mutationFn: async () => {
      if (!org || !ctx) throw new Error('no org');
      const created = await supportApi.create(org.api, {
        org_id: org.org_id,
        category,
        subject: subject.trim(),
        body: body.trim(),
        device_ref: dev?.device_ref,
        device_model: dev?.model,
        language: i18n.language,
        app_version: `${config.version || 'dev'} (${build})`,
        override_reason: override.trim() || undefined,
      });
      // 附件逐個上傳：失敗不影響回報本身，畫面講有幾個沒傳上去（同 SPEC #support-dev-app）
      let failed = 0;
      const uploads: [Blob, string][] = [[new Blob([contextJson(ctx)], { type: 'application/json' }), 'context.json'], ...files.map((f) => [f, f.name] as [Blob, string])];
      for (const [blob, name] of uploads) {
        try {
          await supportApi.attach(org.api, created.ticket_id, blob, name);
        } catch {
          failed++;
        }
      }
      return { id: created.ticket_id, failed };
    },
    onSuccess: ({ id, failed }) => {
      void qc.invalidateQueries({ queryKey: ['support'] });
      setFailedUploads(failed);
      nav(`/support/${encodeURIComponent(id)}${failed ? `?upload_failed=${failed}` : ''}`);
    },
    onError: (e) => {
      if (e instanceof KinioError && e.code === 'REVIEW_BLOCKED') {
        const d = e.detail as { review?: { summary?: string } };
        setBlocked(d?.review?.summary ?? '');
      }
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    m.mutate();
  };

  return (
    <form className="support-form" onSubmit={submit} data-testid="support-form">
      <h1 className="page-title">{t('support.new_title')}</h1>
      <p className="muted small">{t('support.new_hint')}</p>
      {mine.length > 1 && (
        <label className="field">
          <span>{t('support.org')}</span>
          <select className="input" value={orgId} onChange={(e) => setOrgId(e.target.value)} data-testid="support-org">
            {mine.map((o) => (
              <option key={o.org_id} value={o.org_id}>
                {o.display_name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="field">
        <span>{t('support.category')}</span>
        <select className="input" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="support-category">
          {SUPPORT_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {t(`support.cat_${c}`)}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>{t('support.device')}</span>
        <select className="input" value={devKey} onChange={(e) => setDevKey(e.target.value)} data-testid="support-device">
          <option value="">{t('support.device_none')}</option>
          {devs.map((d) => (
            <option key={`${d.home_id}/${d.device_ref}`} value={`${d.home_id}/${d.device_ref}`}>
              {d.display_name}
              {d.model ? ` · ${d.model}` : ''}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>{t('support.subject')}</span>
        <input className="input" value={subject} maxLength={200} required onChange={(e) => setSubject(e.target.value)} data-testid="support-subject" />
      </label>
      <label className="field">
        <span>{t('support.body')}</span>
        <textarea className="input" rows={6} value={body} required placeholder={t('support.body_ph')} onChange={(e) => setBody(e.target.value)} data-testid="support-body" />
      </label>
      <label className="field">
        <span>{t('support.files', { n: SUPPORT_MAX_FILES })}</span>
        <input type="file" multiple accept={SUPPORT_ATTACH_TYPES.join(',')} onChange={(e) => pick(e.target.files)} data-testid="support-files" />
      </label>
      {files.length > 0 && (
        <ul className="plain-list small">
          {files.map((f, i) => (
            <li key={i}>
              {f.name}{' '}
              <button type="button" className="link-btn" onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                {t('support.remove')}
              </button>
            </li>
          ))}
        </ul>
      )}
      {fileErr && <p className="error small">{fileErr}</p>}
      {ctx && (
        <details className="card" data-testid="support-context">
          <summary>{t('support.context')}</summary>
          <p className="muted small">{t('support.context_hint')}</p>
          <pre className="support-text">{contextJson(ctx)}</pre>
        </details>
      )}
      {blocked !== null && (
        <div className="card" role="alert" data-testid="support-blocked">
          <p>{t('support.blocked', { summary: blocked || '-' })}</p>
          <label className="field">
            <span>{t('support.override')}</span>
            <input className="input" value={override} onChange={(e) => setOverride(e.target.value)} />
          </label>
        </div>
      )}
      {m.isError && blocked === null && <p className="error">{errMsg(t, m.error)}</p>}
      {failedUploads > 0 && <p className="error small">{t('support.upload_failed', { n: failedUploads })}</p>}
      <div className="toolbar">
        <button className="btn btn-primary" type="submit" disabled={m.isPending || !subject.trim() || !body.trim()} data-testid="support-submit">
          {m.isPending ? t('support.sending') : t('support.submit')}
        </button>
        <Link className="btn" to="/support">
          {t('common.cancel')}
        </Link>
      </div>
    </form>
  );
}

function SupportDetail() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const { orgs } = useScope();
  const apis = reporterApis(orgs);
  const qc = useQueryClient();
  const [text, setText] = useState('');
  // 回報在哪個 plane：照有回報權的組織 plane 依序找（通常只有一個）
  const q = useQuery({
    queryKey: ['support', 'one', id, apis.join(',')],
    queryFn: async () => {
      let last: unknown = null;
      for (const api of apis) {
        try {
          return { ...(await supportApi.get(api, id)), api } as SupportTicket & { api: string };
        } catch (e) {
          last = e;
          if (!(e instanceof KinioError && e.status === 404)) throw e;
        }
      }
      throw last ?? new Error('not found');
    },
    retry: false,
  });
  const send = useMutation({
    mutationFn: async () => {
      if (!q.data) return;
      await supportApi.comment(q.data.api, id, text.trim());
    },
    onSuccess: () => {
      setText('');
      void qc.invalidateQueries({ queryKey: ['support'] });
    },
  });
  const failedUploads = Number(params.get('upload_failed') || 0);
  if (q.isLoading) return <p className="muted">{t('common.loading')}</p>;
  if (q.isError || !q.data)
    return (
      <div>
        <p className="muted">{t('support.not_found')}</p>
        <Link to="/support">{t('support.back')}</Link>
      </div>
    );
  const x = q.data;
  const events = (x.events ?? []).filter((e) => e.kind === 'comment' || e.kind === 'reply' || e.kind === 'created' || e.kind === 'status');
  const who = (e: (typeof events)[number]) =>
    e.actor_role === 'user' ? (e.actor === x.user_id && x.relation === 'owner' ? t('support.me') : e.actor_name || t('support.member')) : t('support.staff');
  return (
    <div data-testid="support-detail">
      <p>
        <Link to="/support">← {t('support.back')}</Link>
      </p>
      <h1 className="page-title">{x.subject}</h1>
      {failedUploads > 0 && <p className="error small">{t('support.upload_failed', { n: failedUploads })}</p>}
      <p>
        <span className={statusBadge(x.status)}>{t(`support.status_${x.status}`, { defaultValue: x.status })}</span>{' '}
        <span className="muted small">
          {t(`support.cat_${x.category}`, { defaultValue: x.category })} · {x.org_name || ''} · {new Date(x.created_at).toLocaleString()}
          {x.device_ref ? ` · ${x.device_model || ''} ${x.device_ref}` : ''}
        </span>
      </p>
      <ul className="plain-list support-timeline">
        {events.map((e) => (
          <li key={e.event_id} className="card" data-testid="support-event">
            <div className="muted small">
              {who(e)} · {new Date(e.created_at).toLocaleString()}
            </div>
            {e.kind === 'status' ? <div className="muted">{t('support.status_changed')}</div> : <div style={{ whiteSpace: 'pre-wrap' }}>{e.body}</div>}
          </li>
        ))}
      </ul>
      {(x.attachments ?? []).length > 0 && (
        <div className="card">
          <strong>{t('support.attachments')}</strong>
          <ul className="plain-list small">
            {(x.attachments ?? []).map((a) => (
              <li key={a.attachment_id}>{a.filename}</li>
            ))}
          </ul>
        </div>
      )}
      {(x.watchers ?? []).length > 0 && (
        <p className="muted small">
          {t('support.watchers')}: {(x.watchers ?? []).map((w) => w.name || w.email || '-').join('、')}
        </p>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) send.mutate();
        }}
      >
        <label className="field">
          <span>{t('support.add_comment')}</span>
          <textarea className="input" rows={3} value={text} onChange={(e) => setText(e.target.value)} data-testid="support-comment" />
        </label>
        {send.isError && <p className="error small">{errMsg(t, send.error)}</p>}
        <button className="btn btn-primary" type="submit" disabled={send.isPending || !text.trim()} data-testid="support-comment-send">
          {t('support.send')}
        </button>
      </form>
    </div>
  );
}
